import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, ClipboardCheck, Printer } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Card } from '@/components/common/Card';
import { EmptyState, ErrorState, LoadingState } from '@/components/common/States';
import { ROUTES } from '@/app/router/routes';
import { ReportCard } from '@/features/examinations/ReportCardPage';
import { portalApi } from '@/services/api/endpoints/portal';
import { queryKeys } from '@/services/api/queryKeys';
import { PortalShell, type PortalContext } from './PortalShell';
import { isNotFound } from './shared';

/**
 * One published report card, printable exactly like the office's copy: the same
 * component renders both, and only the `.report-card` article reaches paper.
 */
export function PortalResultDetailPage() {
  return (
    <PortalShell title="Report card" icon={<ClipboardCheck className="h-5 w-5" aria-hidden="true" />}>
      {(context) => <ResultDetail context={context} />}
    </PortalShell>
  );
}

function ResultDetail({ context }: { context: PortalContext }) {
  const { subject, subjectKey, withChild } = context;
  const { examinationId = '' } = useParams();

  const result = useQuery({
    queryKey: queryKeys.portal.result(subjectKey, examinationId),
    queryFn: () => portalApi.result(subject, examinationId),
    enabled: examinationId !== '',
    retry: false,
  });

  const back = (
    <Link
      to={withChild(ROUTES.myResults)}
      className="inline-flex items-center gap-1.5 text-[13px] text-muted transition-colors hover:text-ink print:hidden"
    >
      <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
      All results
    </Link>
  );

  if (result.isPending) {
    return (
      <Card>
        <LoadingState label="Loading the report card" />
      </Card>
    );
  }

  if (result.isError) {
    return (
      <div>
        <div className="mb-3">{back}</div>
        <Card>
          {isNotFound(result.error) ? (
            <EmptyState
              icon={<ClipboardCheck className="h-6 w-6" aria-hidden="true" />}
              title="This report card is not available"
              description="It has not been published, or it is not on this student's record."
            />
          ) : (
            <ErrorState error={result.error} onRetry={() => void result.refetch()} />
          )}
        </Card>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3 print:hidden">
        {back}
        <Button size="sm" onClick={() => window.print()}>
          <Printer className="h-3.5 w-3.5" aria-hidden="true" />
          Print
        </Button>
      </div>
      <ReportCard card={result.data} />
    </div>
  );
}
